// src/pages/NewPurchasePage.js
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Grid, TextField, Select, MenuItem,
    InputLabel, FormControl, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, CircularProgress, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import api from '../api/index.js';

const NewPurchasePage = () => {
    const navigate = useNavigate();

    // --- ESTADOS ---
    // Estado para el formulario principal
    const [purchaseDetails, setPurchaseDetails] = useState({
        supplier_id: '',
        store_id: '',
        invoice_number: '',
        status: 'Financiada', // Valor por defecto
        due_date: '',
    });

    // Estado para la lista de ítems que se van añadiendo a la compra
    const [purchaseItems, setPurchaseItems] = useState([]);

    // Estado para el formulario de "Añadir Ítem"
    const [currentItem, setCurrentItem] = useState({
        product_id: '',
        product_name: '',
        quantity: 1,
        cost_price: 0,
    });
    
    // Estado para las listas de catálogos (proveedores, tiendas, productos)
    const [suppliers, setSuppliers] = useState([]);
    const [stores, setStores] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    
    // Estados de UI
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // --- CARGA DE DATOS INICIAL ---
    useEffect(() => {
        const fetchPrerequisites = async () => {
            setLoading(true);
            try {
                const [suppliersRes, storesRes, productsRes] = await Promise.all([
                    api.get('/suppliers'),
                    api.get('/stores'),
                    api.get('/products'),
                ]);
                setSuppliers(suppliersRes.data);
                setStores(storesRes.data);
                setAllProducts(productsRes.data);
            } catch (err) {
                setError('No se pudieron cargar los datos necesarios.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchPrerequisites();
    }, []);

    // --- MANEJADORES DE EVENTOS ---
    const handleDetailsChange = (e) => {
        setPurchaseDetails({ ...purchaseDetails, [e.target.name]: e.target.value });
    };

    const handleItemFormChange = (e) => {
        const { name, value } = e.target;
        // Si se cambia el producto del selector, actualizamos también el nombre y el precio de compra
        if (name === 'product_id') {
            const selectedProd = allProducts.find(p => p.id === value);
            setCurrentItem({
                ...currentItem,
                product_id: value,
                product_name: selectedProd.name,
                cost_price: selectedProd.purchase_price || 0,
            });
        } else {
            setCurrentItem({ ...currentItem, [name]: value });
        }
    };

    const handleAddItem = () => {
        if (!currentItem.product_id || currentItem.quantity <= 0 || currentItem.cost_price < 0) {
            setError('Por favor, selecciona un producto y especifica cantidad y costo válidos.');
            return;
        }
        // Evitar duplicados, en su lugar se podría actualizar la cantidad si ya existe
        if (purchaseItems.some(item => item.product_id === currentItem.product_id)) {
            setError('El producto ya está en la lista. Edítalo o bórralo para volverlo a añadir.');
            return;
        }
        setPurchaseItems([...purchaseItems, currentItem]);
        // Limpiar el formulario de ítem
        setCurrentItem({ product_id: '', product_name: '', quantity: 1, cost_price: 0 });
        setError('');
    };
    
    const handleRemoveItem = (productId) => {
        setPurchaseItems(purchaseItems.filter(item => item.product_id !== productId));
    };

    const handleSavePurchase = async () => {
        setError('');
        if (!purchaseDetails.supplier_id || !purchaseDetails.store_id || purchaseItems.length === 0) {
            setError('Debes completar los detalles de la compra y añadir al menos un producto.');
            return;
        }

        const payload = {
            ...purchaseDetails,
            items: purchaseItems.map(({ product_id, quantity, cost_price }) => ({
                product_id,
                quantity: parseInt(quantity, 10),
                cost_price: parseFloat(cost_price)
            }))
        };

        try {
            setLoading(true);
            await api.post('/purchases', payload);
            alert('¡Compra registrada exitosamente!');
            navigate('/purchases'); // Redirigir al historial
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar la compra.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };
    
    const totalPurchase = purchaseItems.reduce((sum, item) => sum + item.quantity * item.cost_price, 0);

    if (loading && suppliers.length === 0) return <CircularProgress />;

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom>Registrar Nueva Compra</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            {/* SECCIÓN 1: DETALLES DE LA COMPRA */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Detalles de la Factura</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Proveedor</InputLabel>
                            <Select name="supplier_id" value={purchaseDetails.supplier_id} label="Proveedor" onChange={handleDetailsChange}>
                                {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Tienda de Destino</InputLabel>
                            <Select name="store_id" value={purchaseDetails.store_id} label="Tienda de Destino" onChange={handleDetailsChange}>
                                {stores.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="N° Factura" name="invoice_number" value={purchaseDetails.invoice_number} onChange={handleDetailsChange} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Estado de Pago</InputLabel>
                            <Select name="status" value={purchaseDetails.status} label="Estado de Pago" onChange={handleDetailsChange}>
                                <MenuItem value="Pagada">Pagada</MenuItem>
                                <MenuItem value="Financiada">Financiada</MenuItem>
                                <MenuItem value="Parcialmente Pagada">Parcialmente Pagada</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Fecha de Vencimiento" name="due_date" type="date" value={purchaseDetails.due_date} onChange={handleDetailsChange} InputLabelProps={{ shrink: true }} />
                    </Grid>
                </Grid>
            </Paper>

            {/* SECCIÓN 2: AÑADIR ÍTEMS */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Añadir Productos</Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <FormControl fullWidth>
                            <InputLabel>Producto</InputLabel>
                            <Select name="product_id" value={currentItem.product_id} label="Producto" onChange={handleItemFormChange}>
                                {allProducts.map(p => <MenuItem key={p.id} value={p.id}>{p.name} - {p.sku}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField fullWidth label="Cantidad" name="quantity" type="number" value={currentItem.quantity} onChange={handleItemFormChange} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField fullWidth label="Costo Unitario" name="cost_price" type="number" value={currentItem.cost_price} onChange={handleItemFormChange} />
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <Button fullWidth variant="contained" onClick={handleAddItem} sx={{ height: '56px' }}>Añadir</Button>
                    </Grid>
                </Grid>
            </Paper>
            
            {/* SECCIÓN 3: LISTA DE ÍTEMS Y TOTAL */}
            <Typography variant="h6" gutterBottom>Productos en la Orden</Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Producto</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                            <TableCell align="right">Costo Unitario</TableCell>
                            <TableCell align="right">Subtotal</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {purchaseItems.map(item => (
                            <TableRow key={item.product_id}>
                                <TableCell>{item.product_name}</TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">${Number(item.cost_price).toFixed(2)}</TableCell>
                                <TableCell align="right">${(item.quantity * item.cost_price).toFixed(2)}</TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleRemoveItem(item.product_id)}><DeleteIcon color="error" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>${totalPurchase.toFixed(2)}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {/* SECCIÓN 4: BOTÓN DE GUARDAR */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" color="primary" size="large" onClick={handleSavePurchase} disabled={loading}>
                    {loading ? 'Guardando...' : 'Guardar Compra'}
                </Button>
            </Box>
        </Box>
    );
};

export default NewPurchasePage;